/**
 * Page « Mes applications » (§3.4).
 *
 * La liste reste un inventaire : chaque ligne mène à la **fiche application**
 * (§3.5), d'où partent la mise à jour, la comparaison de versions et la
 * restauration. Le formulaire de mise à jour n'est plus proposé ici — il exige
 * désormais un commentaire de version et rappelle l'adresse verrouillée, ce qui
 * n'a de sens qu'en regard de l'historique.
 *
 * Le texte de la confirmation de suppression dépend de `userCanDelete` : selon
 * la configuration serveur, l'action supprime réellement l'application ou se
 * contente de transmettre une demande au service informatique. On le dit
 * clairement avant de valider, plutôt que de laisser l'utilisateur découvrir
 * après coup que rien n'a été supprimé.
 */

import type { AppSummaryDto } from '@portail/shared';
import { Activity, ExternalLink, Pencil, Rocket, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { listMyApps, requestAppDeletion } from '../api';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { DismissibleBadge } from '../components/DismissibleBadge';
import { AppStatusBadge } from '../components/status';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorBanner,
  LinkButton,
  PageHeader,
  Skeleton,
  TableScroll,
} from '../components/ui';
import { messageOf, useAsync } from '../hooks/useAsync';
import { useSession } from '../hooks/useSession';
import { dismissalKey } from '../lib/dismissed';
import { formatDate, formatNumber, formatRelative } from '../lib/format';

/** Squelette de la liste. */
function AppsSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }, (_unused, index) => (
        <Card key={index} className="p-4">
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="mt-3 h-4 w-2/3" />
          <Skeleton className="mt-2 h-4 w-1/4" />
        </Card>
      ))}
    </div>
  );
}

/** Ligne du tableau des applications. */
function AppRow({ app, onDelete }: { app: AppSummaryDto; onDelete: (app: AppSummaryDto) => void }) {
  return (
    <tr className="border-t border-slate-200 align-top dark:border-slate-800">
      <td className="px-4 py-3">
        {/* Le nom mène à la fiche application : c'est de là que partent la mise
            à jour, le comparatif de versions et la restauration (§3.4). */}
        <Link
          to={`/mes-applications/${app.id}`}
          className="font-medium text-slate-900 underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:text-slate-100"
        >
          {app.slug}
        </Link>
        <br />
        <a
          href={app.url}
          target="_blank"
          rel="noreferrer"
          className="mt-0.5 inline-flex items-center gap-1 break-all font-mono text-xs text-indigo-700 underline underline-offset-2 hover:text-indigo-600 dark:text-indigo-300"
        >
          {app.url}
          <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
        </a>
        {app.deletionRequestedAt ? (
          <div className="mt-1">
            <Badge tone="amber">Suppression demandée</Badge>
          </div>
        ) : app.deletionRefusedAt ? (
          /*
            Le refus est une information, pas un silence : sans lui, l'auteur
            redemande la semaine suivante sans savoir ce qui a été décidé. Mais il
            doit pouvoir le ranger une fois lu — la pastille restait sinon sur sa
            liste indéfiniment. La clé porte la date de la décision : un nouveau
            refus réapparaîtra, et le motif reste lisible sur la fiche.
          */
          <div className="mt-1">
            <DismissibleBadge
              tone="red"
              label="Suppression refusée"
              storageKey={dismissalKey('refus', app.id, app.deletionRefusedAt)}
            >
              Suppression refusée
            </DismissibleBadge>
          </div>
        ) : null}
      </td>
      <td className="px-4 py-3">
        <AppStatusBadge status={app.status} />
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm">
        {app.currentVersionNumber !== null ? (
          <span className="font-medium tabular-nums text-slate-900 dark:text-slate-100">
            v{app.currentVersionNumber}
          </span>
        ) : (
          <span className="text-slate-400 dark:text-slate-600" title="Aucune version en ligne">
            —
          </span>
        )}
        {typeof app.versionCount === 'number' && app.versionCount > 1 ? (
          <span className="ml-1 text-xs text-slate-500 dark:text-slate-400">
            sur {formatNumber(app.versionCount)}
          </span>
        ) : null}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        {formatDate(app.createdAt)}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        {formatRelative(app.lastDeployedAt)}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums text-slate-700 dark:text-slate-300">
        {app.visits7d === null ? (
          <span className="text-slate-400 dark:text-slate-600" title="Statistiques indisponibles">
            —
          </span>
        ) : (
          formatNumber(app.visits7d)
        )}
      </td>
      <td className="max-w-xs px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
        {app.comment ? (
          <span className="line-clamp-3">{app.comment}</span>
        ) : (
          <span className="text-slate-400 dark:text-slate-600">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        {/* Une seule rangée, jamais de retour à la ligne : avec `flex-wrap`, un
            troisième bouton passait dessous et l'alignement partait de travers.
            Le tableau défile déjà horizontalement s'il le faut. */}
        <div className="flex flex-nowrap items-center justify-end gap-2">
          {/* Un déploiement en cours n'était joignable que depuis l'écran de
              progression : le quitter le rendait introuvable, alors que le
              travail se poursuit côté serveur. */}
          {app.activeDeploymentId ? (
            <LinkButton
              to={`/deploiements/${app.activeDeploymentId}`}
              variant="primary"
              className="whitespace-nowrap"
            >
              <Activity className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Suivre
            </LinkButton>
          ) : null}
          <LinkButton to={`/mes-applications/${app.id}`} className="whitespace-nowrap">
            <Pencil className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            Versions et mise à jour
          </LinkButton>
          <Button
            variant="ghost"
            onClick={() => onDelete(app)}
            aria-label={`Supprimer ${app.slug}`}
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" aria-hidden="true" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

/** Liste des applications de l'utilisateur connecté. */
export function MyAppsPage() {
  const { settings } = useSession();
  const apps = useAsync(() => listMyApps(), []);

  const [deleting, setDeleting] = useState<AppSummaryDto | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /**
   * Une application dont aucun déploiement n'a abouti n'a jamais été exposée :
   * son auteur la supprime lui-même. La règle est décidée par le serveur ; elle
   * est reprise ici uniquement pour annoncer à l'avance ce qui va se passer.
   */
  const jamaisEnLigne = deleting !== null && (deleting.versionCount ?? 0) === 0;
  const suppressionImmediate = jamaisEnLigne || settings.userCanDelete;

  const confirmDeletion = (): void => {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);

    requestAppDeletion(deleting.id, null)
      .then((response) => {
        // Le message vient du serveur : il reflète la configuration réelle.
        setNotice(response.message);
        setDeleting(null);
        setDeleteBusy(false);
        apps.reload();
      })
      .catch((error: unknown) => {
        setDeleteError(messageOf(error));
        setDeleteBusy(false);
      });
  };

  return (
    <>
      <PageHeader
        title="Mes applications"
        description="Les applications que vous avez déployées, leur version en ligne, leur état en direct et leur fréquentation sur les sept derniers jours. Ouvrez une fiche pour la mettre à jour, comparer ses versions ou en restaurer une."
        actions={
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          >
            <Rocket className="h-4 w-4" aria-hidden="true" />
            Nouveau déploiement
          </Link>
        }
      />

      {notice ? (
        <div
          role="status"
          className="mb-4 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-200"
        >
          {notice}
        </div>
      ) : null}

      {apps.loading ? (
        <AppsSkeleton />
      ) : apps.error ? (
        <ErrorBanner message={apps.error} onRetry={apps.reload} />
      ) : (apps.data ?? []).length === 0 ? (
        <EmptyState
          title="Aucune application pour l'instant"
          description="Dès que vous aurez déployé un artifact, il apparaîtra ici avec son adresse, son état et ses statistiques de visite."
          action={
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500"
            >
              <Rocket className="h-4 w-4" aria-hidden="true" />
              Déployer ma première application
            </Link>
          }
        />
      ) : (
        <Card>
          <TableScroll>
            <table className="min-w-full text-left">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-950 dark:text-slate-400">
                <tr>
                  <th scope="col" className="px-4 py-2 font-medium">
                    Application
                  </th>
                  <th scope="col" className="px-4 py-2 font-medium">
                    Statut
                  </th>
                  <th scope="col" className="px-4 py-2 font-medium">
                    Version en ligne
                  </th>
                  <th scope="col" className="px-4 py-2 font-medium">
                    Créée le
                  </th>
                  <th scope="col" className="px-4 py-2 font-medium">
                    Dernier déploiement
                  </th>
                  {/* `whitespace-nowrap` : sans lui, l'en-tête se coupait entre
                      « 7 » et « j », laissant une lettre seule sur la ligne. */}
                  <th scope="col" className="px-4 py-2 text-right font-medium whitespace-nowrap">
                    Visites 7 j
                  </th>
                  <th scope="col" className="px-4 py-2 font-medium">
                    Commentaire
                  </th>
                  <th scope="col" className="px-4 py-2 text-right font-medium">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {(apps.data ?? []).map((app) => (
                  <AppRow
                    key={app.id}
                    app={app}
                    onDelete={(target) => {
                      setDeleteError(null);
                      setDeleting(target);
                    }}
                  />
                ))}
              </tbody>
            </table>
          </TableScroll>
        </Card>
      )}

      <ConfirmDialog
        open={deleting !== null}
        title={suppressionImmediate ? 'Supprimer cette application ?' : 'Demander la suppression ?'}
        description={
          // Trois cas bien distincts : un essai qui n'a jamais abouti se
          // supprime seul, une application en ligne engage ses utilisateurs.
          jamaisEnLigne ? (
            <>
              Aucun déploiement de <span className="font-mono">{deleting?.slug}</span> n'a abouti :
              rien n'a été mis en ligne. La supprimer libère le nom, qui redeviendra disponible
              immédiatement. Les journaux restent consultables par le service informatique.
            </>
          ) : settings.userCanDelete ? (
            <>
              L'application <span className="font-mono">{deleting?.slug}</span> sera retirée du
              Cloudron et son adresse cessera de répondre. Les journaux de déploiement restent
              consultables par le service informatique.
            </>
          ) : (
            <>
              <span className="font-mono">{deleting?.slug}</span> a déjà été mise en ligne : sa
              suppression est soumise au service informatique, qui l'acceptera ou la refusera en
              vous en donnant la raison. L'application reste accessible jusque-là.
            </>
          )
        }
        confirmLabel={suppressionImmediate ? 'Supprimer' : 'Transmettre la demande'}
        destructive={suppressionImmediate}
        busy={deleteBusy}
        error={deleteError}
        onConfirm={confirmDeletion}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}
